import {
  CreateLtlePayload,
  TourOffer,
  TourOfferBookingTerms,
  TourOfferConfigurationContent,
  TourOfferContent,
  TourOfferDeparture,
  TourOfferSummary,
} from '~/types/tours/tourOffer';

import { json_headers, request } from './common';
import { OkResponse, toursBasePath } from './toursApi';

export async function searchTourOffers(
  params: { search?: string; ltleOnly?: boolean },
  options?: { signal?: AbortSignal },
): Promise<Array<TourOfferSummary>> {
  const search = new URLSearchParams();
  if (params.search) {
    search.set('search', params.search);
  }
  if (params.ltleOnly) {
    search.set('ltleOnly', 'true');
  }
  const query = search.toString();

  const response: OkResponse<Array<TourOfferSummary>> = await request(
    `${toursBasePath()}/tour-offers${query ? `?${query}` : ''}`,
    { method: 'GET', signal: options?.signal },
  );

  return response.result;
}

export async function getTourOfferDepartures(
  tourOfferId: string,
  options?: { signal?: AbortSignal },
): Promise<Array<TourOfferDeparture>> {
  const response: OkResponse<Array<TourOfferDeparture>> = await request(
    `${toursBasePath()}/tour-offers/${tourOfferId}/departures`,
    { method: 'GET', signal: options?.signal },
  );

  return response.result;
}

// By tour offer id, so a native-only LTLE (no legacy tour) can be read and edited.
function tourOfferPath(tourOfferId: string): string {
  return `${toursBasePath()}/tour-offers/${tourOfferId}`;
}

export async function getTourOffer(tourOfferId: string, options?: { signal?: AbortSignal }): Promise<TourOffer> {
  const response: OkResponse<TourOffer> = await request(tourOfferPath(tourOfferId), {
    method: 'GET',
    signal: options?.signal,
  });
  return response.result;
}

export async function renameTourOffer(tourOfferId: string, name: string): Promise<TourOffer> {
  const response: OkResponse<TourOffer> = await request(tourOfferPath(tourOfferId), {
    method: 'PATCH',
    headers: json_headers,
    body: JSON.stringify({ name }),
  });
  return response.result;
}

export async function putTourOfferContent(tourOfferId: string, content: TourOfferContent): Promise<TourOffer> {
  const response: OkResponse<TourOffer> = await request(`${tourOfferPath(tourOfferId)}/content`, {
    method: 'PUT',
    headers: json_headers,
    body: JSON.stringify(content),
  });
  return response.result;
}

export async function putTourOfferBookingTerms(
  tourOfferId: string,
  bookingTerms: TourOfferBookingTerms,
): Promise<TourOffer> {
  const response: OkResponse<TourOffer> = await request(`${tourOfferPath(tourOfferId)}/booking-terms`, {
    method: 'PUT',
    headers: json_headers,
    body: JSON.stringify(bookingTerms),
  });

  return response.result;
}

export async function putTourOfferConfigurationContent(
  tourOfferId: string,
  configurationId: string,
  content: TourOfferConfigurationContent,
): Promise<TourOffer> {
  const response: OkResponse<TourOffer> = await request(
    `${tourOfferPath(tourOfferId)}/configurations/${configurationId}/content`,
    { method: 'PUT', headers: json_headers, body: JSON.stringify(content) },
  );
  return response.result;
}

/** Creates a native LTLE from a base tour offer; its service links back to the base. */
export async function createLtle(baseTourOfferId: string, payload: CreateLtlePayload): Promise<TourOfferSummary> {
  const response: OkResponse<TourOfferSummary> = await request(
    `${toursBasePath()}/tour-offers/${baseTourOfferId}/ltles`,
    {
      method: 'POST',
      headers: json_headers,
      body: JSON.stringify(payload),
    },
  );

  return response.result;
}
